import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, HostBinding, HostListener, Signal } from '@angular/core';
import { SidebarService } from '../../shared/services/sidebar.service';
import { SidebarUiService } from '../../shared/services/sidebar-ui.service';
import { ThemeService } from '../../shared/services/theme.service';
import { SecurityService } from '../../services/security/security.service';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { take, takeUntil, map, filter } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { AuthImagePipe } from '../../pipes/auth-image.pipe';
import { Usuario } from '../../domain/usuario.domain';
import { environment } from '../../../environments/environment';

interface MenuItem {
  name: string;
  description?: string;
  icon: string;
  path?: string;
  subItems?: SubMenuItem[];
}

interface SubMenuItem {
  name: string;
  path: string;
  icon?: string;
}

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    SafeHtmlPipe,
    AuthImagePipe
  ],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.scss',
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  public sidebarService = inject(SidebarService);
  public themeService = inject(ThemeService);
  private router = inject(Router);

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;
  readonly showLabels: Signal<boolean>;

  isUserMenuOpen = false;
  readonly ui = inject(SidebarUiService);

  @HostBinding('style.--sidebar-font-scale') get hostFontScale(): string {
    return String(this.ui.fontScale());
  }
  usuario: Usuario = {};
  avatarUrl: string | null = null;
  private securityService = inject(SecurityService);
  private destroy$ = new Subject<void>();

  menuItems: MenuItem[] = [
    {
      name: 'Dashboard',
      description: 'Panel principal',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>`,
      path: '/',
      subItems: [
        {
          name: 'Analytics',
          path: '/analytics',
          icon: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>`
        },
        {
          name: 'Reportes',
          path: '/reportes',
          icon: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>`
        }
      ]
    },
    {
      name: 'Usuarios',
      description: 'Gestión de usuarios',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>`,
      path: '/usuarios',
      subItems: [
        {
          name: 'Lista de usuarios',
          path: '/usuarios',
          icon: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>`
        },
        {
          name: 'Roles y permisos',
          path: '/usuarios/roles',
          icon: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>`
        }
      ]
    },
    {
      name: 'Clientes',
      description: 'Gestión de clientes',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>`,
      path: '/clientes'
    },
    {
      name: 'Órdenes',
      description: 'Órdenes de publicidad',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>`,
      path: '/ordenes'
    },
    {
      name: 'Tarifas',
      description: 'Gestión de tarifas',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>`,
      path: '/tarifas'
    },
    {
      name: 'Programas',
      description: 'Gestión de programas',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>`,
      path: '/programas'
    },
    {
      name: 'Programación',
      description: 'Programación radial',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>`,
      subItems: [
        {
          name: 'Programación Diaria',
          path: '/programacion-diaria',
          icon: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>`
        },
        {
          name: 'Ver Programación',
          path: '/programacion',
          icon: `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>`
        }
      ]
    },
    {
      name: 'Tipos de Material',
      description: 'Gestión de tipos de material',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>`,
      path: '/tipos-material'
    },
    {
      name: 'Rubros',
      description: 'Gestión de rubros',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
      </svg>`,
      path: '/rubros'
    },
    {
      name: 'Materiales',
      description: 'Gestión de materiales',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
      </svg>`,
      path: '/materiales'
    },
    {
      name: 'Tipos de Producto',
      description: 'Gestión de tipos de producto',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>`,
      path: '/tipo-productos'
    },
    {
      name: 'Formatos Digitales',
      description: 'Gestión de formatos digitales',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h10M4 18h7" />
      </svg>`,
      path: '/formatos-digitales'
    },
    {
      name: 'Ubicaciones Digitales',
      description: 'Gestión de ubicaciones digitales',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z" />
      </svg>`,
      path: '/ubicaciones-digitales'
    },
    {
      name: 'Perfil',
      description: 'Mi cuenta',
      icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>`,
      path: '/profile'
    }
  ];

  constructor() {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;
    this.showLabels = toSignal(
      combineLatest([this.isExpanded$, this.isHovered$, this.isMobileOpen$]).pipe(
        map(([e, h, m]) => e || h || m)
      ),
      { initialValue: false }
    );
  }

  closeDrawer(): void {
    this.sidebarService.setMobileOpen(false);
  }

  toggleRailExpand(): void {
    if (this.ui.overlay()) {
      this.sidebarService.setMobileOpen(true);
    } else {
      this.sidebarService.toggleExpanded();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.ui.overlay()) this.sidebarService.setMobileOpen(false);
  }

  onSidebarMouseEnter() {
    this.isExpanded$.subscribe((expanded: boolean) => {
      if (!expanded) {
        this.sidebarService.setHovered(true);
      }
    }).unsubscribe();
  }

  toggleSubmenu(itemName: string, event?: Event) {
    let isExpanded = false;
    this.isExpanded$.pipe(take(1)).subscribe(expanded => { isExpanded = expanded; }).unsubscribe();
    this.isHovered$.pipe(take(1)).subscribe(hovered => { isExpanded = isExpanded || hovered; }).unsubscribe();
    this.isMobileOpen$.pipe(take(1)).subscribe(mobileOpen => { isExpanded = isExpanded || mobileOpen; }).unsubscribe();

    if (isExpanded && event) {
      event.preventDefault();
      event.stopPropagation();
      const current = this.ui.openSectionId();
      this.ui.setSectionOpen(current === itemName ? null : itemName);
    }
  }

  isSubmenuOpen(itemName: string): boolean {
    return this.ui.openSectionId() === itemName;
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  ngOnInit(): void {
    combineLatest([this.isExpanded$, this.isHovered$, this.isMobileOpen$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([expanded, hovered, mobileOpen]) => {
        const isVisible = expanded || hovered || mobileOpen;
        if (!isVisible) this.ui.setSectionOpen(null);
      });
    const storedUser = this.securityService.getUser();
    if (storedUser) {
      this.usuario = storedUser;
      this.updateAvatarUrl();
    }
    this.securityService.usuario$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user && Object.keys(user).length > 0) {
          this.usuario = user;
          this.updateAvatarUrl();
        }
      });
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.ui.overlay()) this.sidebarService.setMobileOpen(false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  closeUserMenu(): void {
    this.isUserMenuOpen = false;
  }

  logout(): void {
    this.securityService.logout();
    this.closeUserMenu();
  }

  private updateAvatarUrl(): void {
    const mediaId = this.usuario?.media?.id;
    if (mediaId) {
      const baseApi = environment.URL_API.replace(/\/$/, '');
      this.avatarUrl = `${baseApi}/file-manager?action=get&idMedia=${mediaId}`;
      return;
    }
    this.avatarUrl = null;
  }

  getNombreCompleto(): string {
    if (this.usuario?.fullName) {
      return this.usuario.fullName;
    }
    if (this.usuario?.name && this.usuario?.lastName) {
      return `${this.usuario.name} ${this.usuario.lastName}`;
    }
    return this.usuario?.username || 'Usuario';
  }

  getNombreCorto(): string {
    if (this.usuario?.name) {
      return this.usuario.name;
    }
    return this.usuario?.username || 'Usuario';
  }

  /**
   * Filtra los elementos del menú según el rol del usuario
   * Oculta ciertos módulos para ROLE_AGENTE_PUBLICIDAD
   */
  get filteredMenuItems(): MenuItem[] {
    // Si el usuario tiene ROLE_AGENTE_PUBLICIDAD, ocultar ciertos módulos
    if (this.securityService.hasRole('ROLE_AGENTE_PUBLICIDAD')) {
      return this.menuItems.filter(item => {
        // Ocultar estos módulos para ROLE_AGENTE_PUBLICIDAD
        const hiddenModules = ['Usuarios', 'Tarifas', 'Programas', 'Rubros', 'Tipos de Producto'];
        return !hiddenModules.includes(item.name);
      });
    }

    // Si el usuario tiene ROLE_ADMIN_PUBLICIDAD, ocultar solo Usuarios
    if (this.securityService.hasRole('ROLE_ADMIN_PUBLICIDAD')) {
      return this.menuItems.filter(item => item.name !== 'Usuarios');
    }

    // Para otros roles, mostrar todos los elementos
    return this.menuItems;
  }
}
